import moment from 'moment';

/**
 * Payload keys carrying a contract date. The server parses these as dates, so they must be
 * serialised — a raw moment object must never be posted.
 */
const CONTRACT_DATE_KEYS = ['contract_start', 'contract_end'];

/**
 * Payload keys carrying a contract amount. These are posted as-is (the server accepts a numeric
 * string) but must be absent, never blank, when the field was left empty.
 */
const CONTRACT_AMOUNT_KEYS = ['monthly_credit_amount', 'max_users_override'];

/**
 * The optional seat limit. It is the one key that must never reach the server as a non-positive
 * number: the server reads `max_users_override: 0` as a 400, not as "no limit", so a 0 has to be
 * dropped rather than forwarded. `monthly_credit_amount` is deliberately NOT treated this way —
 * it is required, so a 0 there should surface as a server error rather than silently vanish and
 * turn the request into a partial contract.
 */
const SEAT_LIMIT_KEY = 'max_users_override';

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * A value the user never filled in. `NaN` is included because an empty numeric input read through
 * RHF's `valueAsNumber` produces one, and `JSON.stringify` emits that as `null` — which the server
 * reads as a supplied-but-invalid value rather than as "not supplied".
 *
 * @param {*} value
 * @returns {boolean}
 */
const isBlank = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'number' && Number.isNaN(value)) ||
  String(value).trim() === '';

/**
 * Serialises the enterprise contract fields of one Add Customer entry for
 * `POST /customer/create`.
 *
 * Two rules the endpoint is strict about:
 *  - Dates go over the wire as `YYYY-MM-DD`. An unparseable date is dropped rather than posted as
 *    the string "Invalid date".
 *  - Blank optional fields are **deleted**, not zeroed. `max_users_override: 0` is a 400 on the
 *    server, not "no limit", so an empty seat limit must leave the key absent altogether.
 *
 * An entry carrying no contract fields at all (the legacy path) passes through untouched.
 *
 * @param {Object} customer One entry from the Add Customer form's `customers` field array.
 * @returns {Object} A new entry, safe to post. The argument is not mutated.
 */
const buildContractPayload = (customer) => {
  const payload = { ...customer };

  CONTRACT_DATE_KEYS.forEach((key) => {
    if (!(key in payload)) return;

    const date = moment(payload[key]);
    if (isBlank(payload[key]) || !date.isValid()) delete payload[key];
    else payload[key] = date.format(DATE_FORMAT);
  });

  CONTRACT_AMOUNT_KEYS.forEach((key) => {
    if (key in payload && isBlank(payload[key])) delete payload[key];
  });

  // Defence in depth: the form's own validator already rejects a non-positive seat limit, but this
  // module is the single serialisation boundary, so the guarantee belongs here too.
  if (SEAT_LIMIT_KEY in payload && Number(payload[SEAT_LIMIT_KEY]) <= 0)
    delete payload[SEAT_LIMIT_KEY];

  return payload;
};

export default buildContractPayload;
